/**
 * Generator configuration — loads and validates agents.conf.yaml
 */

import { readFile } from "node:fs/promises";
import { resolve, dirname } from "node:path";
import { z } from "zod";
import YAML from "yaml";

export const DEFAULT_CONFIG_PATH = "./agents.conf.yaml";

const RecoverySchema = z.object({
  max_follow_ups: z.number().int().min(0).default(2),
  max_retries: z.number().int().min(0).default(0),
}).default(() => ({ max_follow_ups: 2, max_retries: 0 }));

const ModelClassEntrySchema = z.object({
  adapter: z.string(),
  model: z.string().optional(),
});

const ModelMappingSchema = z.object({
  fast: ModelClassEntrySchema.optional(),
  standard: ModelClassEntrySchema.optional(),
  thinking: ModelClassEntrySchema.optional(),
}).default({});

const ProgressLogSchema = z.object({
  destination: z.enum(["stderr", "file", "both", "none"]).default("stderr"),
  file: z.string().optional(),
  naming: z.enum(["single", "per-invocation", "daily"]).default("single"),
}).default(() => ({ destination: "stderr" as const, naming: "single" as const }))
  .refine(
    (val) => {
      if ((val.destination === "file" || val.destination === "both") && !val.file) return false;
      return true;
    },
    { message: "logging.progress_log.file is required when destination is 'file' or 'both'" },
  );

const LoggingSchema = z.object({
  progress_log: ProgressLogSchema,
}).default(() => ({ progress_log: { destination: "stderr" as const, naming: "single" as const } }));

export const RuntimeConfigSchema = z.object({
  dsl: z.string().default("./agent-contracts.yaml"),
  generated_dir: z.string().default("./agent/generated"),
  bindings: z.array(z.string()).default([]),
  active_guardrail_policy: z.string().optional(),
  templates_dir: z.string().optional(),
  plugins: z.array(z.string()).default([]),
  recovery: RecoverySchema,
  model_mapping: ModelMappingSchema,
  logging: LoggingSchema,
});

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

/**
 * Unified project bindings object.
 *   - guardrail: guardrail_impl matcher files → machine enforcement (BoundResolver)
 *   - hooks:     git hook bindings → governance materialize (#68; declarative for now)
 *   - observability: event-mapping file + external recorder CLI
 */
const BindingsSchema = z.object({
  guardrail: z.array(z.string()).optional(),
  hooks: z.array(z.string()).optional(),
  observability: z.object({
    event_mapping: z.string().optional(),
    recorder: z.string().optional(),
    /** Toggle in-process agent-axis recording. Defaults to ON when this binding exists. */
    enabled: z.boolean().optional(),
    /** Override the SQLite DB path for in-process recording (default: DEFAULT_DB_PATH). */
    db_path: z.string().optional(),
    /**
     * OTel Collector endpoint for emitting spans to an OTLP-compatible backend.
     * When set, @aaac/observability's OtelEmitter is configured with this endpoint.
     * Falls back to process.env.OTEL_EXPORTER_OTLP_ENDPOINT when absent.
     * Omitting this field keeps the OtelEmitter in no-op mode (fail-open).
     */
    otel: z.object({
      endpoint: z.string(),
    }).optional(),
  }).optional(),
}).optional();

const CliSectionSchema = z.object({
  agents: z.string().optional(),
  dsl: z.string().optional(),
  generated_dir: z.string().optional(),
  plugins: z.array(z.string()).optional(),
  bindings: BindingsSchema,
}).strict();

export const ComponentOverrideSchema = z.object({
  agents: z.string().optional(),
  dsl: z.string().optional(),
  component_contract: z.string().optional(),
  generated_dir: z.string().optional(),
  bindings: BindingsSchema,
  plugins: z.array(z.string()).optional(),
  model_mapping: ModelMappingSchema.optional(),
  recovery: RecoverySchema.optional(),
  templates_dir: z.string().optional(),
}).strict();

export type ComponentOverride = z.infer<typeof ComponentOverrideSchema>;

const EnvironmentsSchema = z.object({
  git: z.object({
    hooks: z.array(z.string()).optional(),
  }).optional(),
  cursor: z.object({
    hooks_json: z.string().optional(),
  }).optional(),
  ci: z.object({
    provider: z.string().optional(),
  }).optional(),
}).optional();

export const ProjectConfigSchema = z.object({
  schema: z.string().optional(),
  agents: z.string().optional(),
  dsl: z.string().optional(),
  generated_dir: z.string().optional(),
  bindings: BindingsSchema,
  active_guardrail_policy: z.string().optional(),
  templates_dir: z.string().optional(),
  plugins: z.array(z.string()).optional(),
  recovery: RecoverySchema,
  model_mapping: ModelMappingSchema,
  logging: LoggingSchema,
  artifacts: z.string().optional(),
  toolchains: z.string().optional(),
  components: z.record(z.string(), ComponentOverrideSchema).optional(),
  environments: EnvironmentsSchema,
  cli: z.record(z.string(), CliSectionSchema).optional(),
});

export type ProjectConfig = z.infer<typeof ProjectConfigSchema>;

export interface ResolvedProgressLog {
  destination: "stderr" | "file" | "both" | "none";
  file: string | undefined;
  naming: "single" | "per-invocation" | "daily";
}

export interface ResolvedEnvironments {
  git?: { hooks: string[] };
  cursor?: { hooks_json: string };
  ci?: { provider: string };
}

export interface ResolvedObservability {
  eventMappingPath?: string;
  recorder?: string;
  /** Whether in-process agent-axis recording is enabled (default true when binding present). */
  enabled?: boolean;
  /** Resolved override for the SQLite DB path (undefined → DEFAULT_DB_PATH). */
  dbPath?: string;
  /**
   * OTel Collector endpoint configuration.
   * When present, OtelEmitter is configured with this endpoint at pipeline creation.
   * Falls back to OTEL_EXPORTER_OTLP_ENDPOINT env var when absent.
   */
  otel?: { endpoint: string };
}

export interface ResolvedConfig extends RuntimeConfig {
  configDir: string;
  dslPath: string;
  generatedDir: string;
  /** Resolved guardrail_impl matcher files (machine enforcement). */
  bindingPaths: string[];
  /** Resolved git-hook binding files (governance materialize, #68). */
  hookBindingPaths: string[];
  /** Observability event mapping + recorder, if configured. */
  observability?: ResolvedObservability;
  templatesDir: string | undefined;
  pluginPaths: string[];
  resolvedLogging: { progressLog: ResolvedProgressLog };
  artifactsPath?: string;
  toolchainsPath?: string;
  environments?: ResolvedEnvironments;
  componentOverrides?: Record<string, ComponentOverride>;
}

interface GovernanceFields {
  artifacts?: string;
  toolchains?: string;
  environments?: z.infer<typeof EnvironmentsSchema>;
  componentOverrides?: Record<string, ComponentOverride>;
  hookBindings?: string[];
  observability?: {
    event_mapping?: string;
    recorder?: string;
    enabled?: boolean;
    db_path?: string;
    otel?: { endpoint: string };
  };
}

/** Internal runtime base assembled from a project manifest before RuntimeConfigSchema validation. */
interface RuntimeConfigInput {
  dsl?: string;
  generated_dir?: string;
  bindings: string[];
  active_guardrail_policy?: string;
  templates_dir?: string;
  plugins?: string[];
  recovery: z.infer<typeof RecoverySchema>;
  model_mapping: z.infer<typeof ModelMappingSchema>;
  logging: z.infer<typeof LoggingSchema>;
}

type ConfigOverride = ComponentOverride | z.infer<typeof CliSectionSchema>;

function resolveConfigFilePath(configPath: string): string {
  return resolve(configPath);
}

function mergeBindingsUnion(root: string[] | undefined, override: string[] | undefined): string[] {
  const base = root ?? [];
  const extra = override ?? [];
  const seen = new Set(base);
  const result = [...base];
  for (const binding of extra) {
    if (!seen.has(binding)) {
      result.push(binding);
      seen.add(binding);
    }
  }
  return result;
}

function mergeConfigWithOverride(
  base: RuntimeConfigInput,
  override: ConfigOverride,
): RuntimeConfigInput {
  const merged: RuntimeConfigInput = { ...base };

  const overrideDsl = override.agents ?? override.dsl;
  if (overrideDsl !== undefined) merged.dsl = overrideDsl;
  if (override.generated_dir !== undefined) merged.generated_dir = override.generated_dir;
  if (override.bindings?.guardrail !== undefined) {
    merged.bindings = mergeBindingsUnion(base.bindings, override.bindings.guardrail);
  }
  if (override.plugins !== undefined) merged.plugins = override.plugins;

  if ("templates_dir" in override && override.templates_dir !== undefined) {
    merged.templates_dir = override.templates_dir;
  }

  if ("model_mapping" in override && override.model_mapping) {
    merged.model_mapping = {
      ...base.model_mapping,
      ...(override.model_mapping.fast ? { fast: override.model_mapping.fast } : {}),
      ...(override.model_mapping.standard ? { standard: override.model_mapping.standard } : {}),
      ...(override.model_mapping.thinking ? { thinking: override.model_mapping.thinking } : {}),
    };
  }

  if ("recovery" in override && override.recovery) {
    merged.recovery = {
      ...base.recovery,
      ...override.recovery,
    };
  }

  return merged;
}

function resolveEnvironments(
  environments: z.infer<typeof EnvironmentsSchema>,
  configDir: string,
): ResolvedEnvironments | undefined {
  if (!environments) return undefined;

  const resolved: ResolvedEnvironments = {};

  if (environments.git?.hooks) {
    resolved.git = { hooks: environments.git.hooks };
  }

  if (environments.cursor?.hooks_json) {
    resolved.cursor = {
      hooks_json: resolve(configDir, environments.cursor.hooks_json),
    };
  }

  if (environments.ci?.provider) {
    resolved.ci = { provider: environments.ci.provider };
  }

  return Object.keys(resolved).length > 0 ? resolved : undefined;
}

function buildResolvedConfig(
  config: RuntimeConfig,
  configDir: string,
  governance?: GovernanceFields,
): ResolvedConfig {
  const pl = config.logging.progress_log;

  return {
    ...config,
    configDir,
    dslPath: resolve(configDir, config.dsl),
    generatedDir: resolve(configDir, config.generated_dir),
    bindingPaths: config.bindings.map((b) => resolve(configDir, b)),
    hookBindingPaths: (governance?.hookBindings ?? []).map((b) => resolve(configDir, b)),
    observability: governance?.observability
      ? {
          eventMappingPath: governance.observability.event_mapping
            ? resolve(configDir, governance.observability.event_mapping)
            : undefined,
          recorder: governance.observability.recorder,
          enabled: governance.observability.enabled,
          dbPath: governance.observability.db_path
            ? resolve(configDir, governance.observability.db_path)
            : undefined,
          otel: governance.observability.otel,
        }
      : undefined,
    templatesDir: config.templates_dir
      ? resolve(configDir, config.templates_dir)
      : undefined,
    pluginPaths: config.plugins,
    resolvedLogging: {
      progressLog: {
        destination: pl.destination,
        file: pl.file ? resolve(configDir, pl.file) : undefined,
        naming: pl.naming,
      },
    },
    artifactsPath: governance?.artifacts
      ? resolve(configDir, governance.artifacts)
      : undefined,
    toolchainsPath: governance?.toolchains
      ? resolve(configDir, governance.toolchains)
      : undefined,
    environments: resolveEnvironments(governance?.environments, configDir),
    componentOverrides: governance?.componentOverrides,
  };
}

/** Assemble the internal runtime base from a parsed project manifest (agents wins over dsl alias). */
function projectToRuntimeBase(projectConfig: ProjectConfig): RuntimeConfigInput {
  return {
    dsl: projectConfig.agents ?? projectConfig.dsl,
    generated_dir: projectConfig.generated_dir,
    bindings: projectConfig.bindings?.guardrail ?? [],
    active_guardrail_policy: projectConfig.active_guardrail_policy,
    templates_dir: projectConfig.templates_dir,
    plugins: projectConfig.plugins,
    recovery: projectConfig.recovery,
    model_mapping: projectConfig.model_mapping,
    logging: projectConfig.logging,
  };
}

function resolveOverride(
  projectConfig: ProjectConfig,
  target?: string,
): ConfigOverride | undefined {
  if (!target) return undefined;

  if (projectConfig.components?.[target]) {
    return projectConfig.components[target];
  }

  if (projectConfig.cli?.[target]) {
    return projectConfig.cli[target];
  }

  return undefined;
}

export async function loadProjectConfig(
  configPath: string,
  target?: string,
): Promise<ResolvedConfig> {
  const absPath = resolveConfigFilePath(configPath);
  const raw = await readFile(absPath, "utf8");
  const parsed = YAML.parse(raw);
  const projectConfig = ProjectConfigSchema.parse(parsed);

  let base = projectToRuntimeBase(projectConfig);
  const override = resolveOverride(projectConfig, target);

  if (override) {
    base = mergeConfigWithOverride(base, override);
  }

  const config = RuntimeConfigSchema.parse(base);
  const configDir = dirname(absPath);

  return buildResolvedConfig(config, configDir, {
    artifacts: projectConfig.artifacts,
    toolchains: projectConfig.toolchains,
    environments: projectConfig.environments,
    componentOverrides: projectConfig.components,
    hookBindings: projectConfig.bindings?.hooks,
    observability: projectConfig.bindings?.observability,
  });
}
